import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import BlogCard from '../../components/BlogCard';
import { BlogPostDetail, czechDate, readingTime } from '../../components/blogUtils';
import useWindowDimensions from '../../hooks/size';
import Seo from '../../components/Seo';
import { isAbsoluteUrl, resolveBlogImage, siteUrl } from '../../Constants';
import './styles/BlogPost.scss';

export default function BlogPost() {
    const [post, setPost] = useState<BlogPostDetail | null>(null);
    const { width } = useWindowDimensions();

    const navigate = useNavigate();
    const { postUrl } = useParams();
    const api = new ApiHelper();

    const relatedCount = width > 1080 ? 3 : width > 720 ? 2 : 1;

    async function fetch() {
        try {
            const res = await api.blogApi.getPost(postUrl!);
            setPost(await res.json());
        } catch (x: any) {
            api.processErrors(x);
        }
    }

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openPost = (url: string) => {
        navigate('/blog/' + url);
        navigate(0);
    };

    // Loadable src for the cover, and an absolute variant for OG/social metadata.
    const coverSrc = resolveBlogImage(post?.image);
    const postImage = coverSrc
        ? (isAbsoluteUrl(coverSrc) ? coverSrc : siteUrl + coverSrc)
        : undefined;

    return (
        <div className={"blogPost" + (post ? "" : " fetching")}>
            {post && (
                <Seo
                    title={post.heading}
                    description={post.description}
                    image={postImage}
                    type="article"
                    canonicalPath={`/blog/${postUrl}`}
                >
                    <script type="application/ld+json">{JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BlogPosting',
                        headline: post.heading,
                        description: post.description,
                        ...(postImage ? { image: postImage } : {}),
                        ...(post.author ? { author: { '@type': 'Person', name: post.author } } : {}),
                        ...(post.timeStamp ? { datePublished: post.timeStamp } : {}),
                        mainEntityOfPage: `${siteUrl}/blog/${postUrl}`,
                        publisher: { '@type': 'Organization', name: 'MyMed' },
                    })}</script>
                </Seo>
            )}
            <article className="article">
                <header className="head">
                    <Link to=".." className="crumb">← Zpět na blog</Link>
                    <h1>{post?.heading}</h1>
                    <p className="perex">{post?.description}</p>
                    <div className="meta">
                        {post?.author && <span className="author">{post.author}</span>}
                        {post?.author && <span className="dot" />}
                        <span>{post && czechDate(post.timeStamp)}</span>
                        <span className="dot" />
                        <span>{post && readingTime(post.text)} min čtení</span>
                    </div>
                </header>

                {post?.image && (
                    <div className="cover">
                        <img src={coverSrc} alt="" />
                    </div>
                )}

                <div className="body">
                    <Markdown remarkPlugins={[remarkGfm]}>{post?.text}</Markdown>
                </div>
            </article>

            {!!post?.posts?.length && (
                <section className="more">
                    <h2>Pokračuj ve čtení</h2>
                    <div className="list">
                        {post.posts.slice(0, relatedCount).map((e) => (
                            <BlogCard key={e.url} post={e} onClick={() => openPost(e.url)} />
                        ))}
                    </div>
                </section>
            )}

            <Link to=".." className="back"><img src="/images/common/back.png" alt="" /></Link>
        </div>
    );
}
